import React, { useState } from 'react'

import {
    Button,
    InputAdornment,
    Switch,
    TextField,
    Typography,
    useTheme,
} from '@material-ui/core'
import { ErrorOutline } from '@material-ui/icons'

import { Container } from '../../components'
import { CreatePremiumInput, Premium } from '../../models/Concession'
import { usePremiums } from '../../hooks/usePremiums'
import { useConcessionStyles } from './styles'

interface Props {
    premium?: Premium
    handleClose: () => void
    onCreated?: (premium: Premium) => void
    defaultActive?: boolean
}

export const PremiumForm: React.FC<Props> = ({
    premium,
    handleClose,
    onCreated,
    defaultActive = false,
}) => {
    const theme = useTheme()
    const classes = useConcessionStyles()
    const { createPremium, updatePremium } = usePremiums()

    const [name, setName] = useState(premium?.name ?? '')
    const [description, setDescription] = useState(premium?.description ?? '')
    const [amount, setAmount] = useState(premium?.amount ?? '')
    const [isActive, setIsActive] = useState(
        premium?.is_active ?? defaultActive,
    )
    const [errors, setErrors] = useState<string[]>([])

    const isSaving = createPremium.isLoading || updatePremium.isLoading

    const handleSave = () => {
        const nextErrors: string[] = []
        if (!name.trim()) nextErrors.push('Name is required.')
        const parsed = Number(amount)
        if (!amount || Number.isNaN(parsed) || parsed <= 0) {
            nextErrors.push('Monthly amount must be greater than 0.')
        }
        if (nextErrors.length > 0) {
            setErrors(nextErrors)
            return
        }
        setErrors([])

        const payload: CreatePremiumInput = {
            name: name.trim(),
            description: description.trim(),
            amount: parsed.toFixed(2),
            is_active: isActive,
        }

        if (premium) {
            updatePremium.mutate(
                { id: premium.id, body: payload },
                { onSuccess: () => handleClose() },
            )
        } else {
            createPremium.mutate(payload, {
                onSuccess: (created) => {
                    onCreated?.(created)
                    handleClose()
                },
            })
        }
    }

    const fieldSpacing = { marginBottom: theme.spacing(2), width: '100%' }

    return (
        <>
            <div className={classes.body}>
                <div className={classes.section}>
                    <div className={classes.sectionHeader}>
                        <span className={classes.sectionTitle}>Premium</span>
                        <span className={classes.sectionHint}>
                            Unit upcharge on top of floor-plan rent
                        </span>
                    </div>
                    <TextField
                        label="Name"
                        variant="outlined"
                        size="small"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        placeholder="e.g. Pool View"
                        style={fieldSpacing}
                    />
                    <TextField
                        label="Description"
                        variant="outlined"
                        size="small"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        rows={4}
                        placeholder="e.g. Unit overlooks the pool."
                        style={fieldSpacing}
                    />
                    <TextField
                        label="Monthly amount"
                        variant="outlined"
                        size="small"
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        required
                        inputProps={{ min: 0.01, step: '0.01' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    $
                                </InputAdornment>
                            ),
                        }}
                        style={{ ...fieldSpacing, marginBottom: 0 }}
                    />
                </div>

                <div className={classes.section}>
                    <div className={classes.toggleCard}>
                        <Container direction="column">
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 600 }}
                            >
                                {isActive ? 'Active' : 'Inactive'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {isActive
                                    ? 'Available to attach on lease offers'
                                    : 'Hidden from new lease offers'}
                            </Typography>
                        </Container>
                        <Switch
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                            color="primary"
                        />
                    </div>
                </div>

                {errors.length > 0 && (
                    <div className={classes.errorBox}>
                        <ErrorOutline
                            fontSize="small"
                            style={{ color: theme.palette.error.main }}
                        />
                        <Container direction="column">
                            {errors.map((err, i) => (
                                <Typography
                                    key={i}
                                    variant="body2"
                                    style={{ color: theme.palette.error.dark }}
                                >
                                    {err}
                                </Typography>
                            ))}
                        </Container>
                    </div>
                )}
            </div>

            <div className={classes.footer}>
                <Button
                    variant="outlined"
                    size="medium"
                    color="secondary"
                    onClick={handleClose}
                    disabled={isSaving}
                    className={classes.cancelButton}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleSave}
                    disabled={isSaving}
                    className={classes.saveButton}
                >
                    {premium ? 'Save Changes' : 'Create Premium'}
                </Button>
            </div>
        </>
    )
}
